/**
 * PATH: packages/bills/src/components/BillAmountsSection.tsx
 * FILE_ID: BILLS-PKG-CMP-AMOUNTS-014
 * PURPOSE: "Amounts" section of the data panel — subtotal / GST / total /
 *          currency inputs plus the optional Extra Charge (Freight / Shipping
 *          / Handling) carve-out and its reveal / remove affordances.
 *
 * WHY: The extra-charge reveal logic is the fiddliest part of the panel body;
 *      grouping it with the money inputs keeps BillDataPanel's render lean.
 *      The reveal latch + draft mutations stay in the parent; this is a
 *      props-in view. Extracted from BillDataPanel.tsx (P4-1 split) — no
 *      behaviour change.
 */

import { Input } from '@quotech/ui';
import type { BillDetail } from '../types';
import { Field, Section } from './BillFieldPrimitives';
import type { SetBillField } from './bill-panel-field';

interface BillAmountsSectionProps {
  merged: Partial<BillDetail>;
  busy: boolean;
  billStatus: BillDetail['status'];
  showExtraChargeFields: boolean;
  showAddExtraChargeButton: boolean;
  onRemoveExtraCharge: () => void;
  onRevealExtraCharge: () => void;
  setField: SetBillField;
}

export function BillAmountsSection({
  merged,
  busy,
  billStatus,
  showExtraChargeFields,
  showAddExtraChargeButton,
  onRemoveExtraCharge,
  onRevealExtraCharge,
  setField,
}: BillAmountsSectionProps) {
  return (
    <Section title="Amounts">
      {/* Subtotal + GST feed the headline Total below. */}
      <div className="grid grid-cols-2 gap-2">
        <Field label="Subtotal">
          <Input
            data-bill-field="subtotal"
            className="tabular-nums"
            value={merged.subtotal ?? ''}
            onChange={(e) => setField('subtotal', e.target.value)}
          />
        </Field>
        <Field label="GST">
          <Input
            data-bill-field="gst"
            className="tabular-nums"
            value={merged.gst ?? ''}
            onChange={(e) => setField('gst', e.target.value)}
          />
        </Field>
      </div>
      {/* Headline Total — the number bookkeepers scan for first. Given its own
          soft primary-tinted band + larger, bolder input so it reads as the
          primary figure without changing the editable-field contract (still
          armable). */}
      <div className="grid grid-cols-[minmax(0,1fr)_6.5rem] gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3">
        <Field label="Total">
          <Input
            data-bill-field="total"
            className="h-9 border-primary/20 bg-card text-sm font-semibold tabular-nums focus:ring-primary/30"
            value={merged.total ?? ''}
            onChange={(e) => setField('total', e.target.value)}
          />
        </Field>
        <Field label="Currency">
          <Input
            data-bill-field="currency"
            className="h-9 w-full min-w-0 bg-card px-2 text-center text-sm uppercase"
            maxLength={3}
            value={merged.currency ?? ''}
            onChange={(e) => setField('currency', e.target.value.toUpperCase())}
          />
        </Field>
      </div>

      {/*
        Optional Extra Charge row (Freight / Shipping / Handling).
        Stays invisible by default; revealed via "+ Add extra
        charge" or auto-revealed when the bill already carries a
        value. Subtotal already includes this on the supplier's
        PDF — we don't add it to the local Subtotal+GST=Total
        invariant. Push to Xero appends it as a discrete LineItem
        (see organisation/views/bill_views/xero_push_view.py
        ::_build_line_items).
      */}
      {showExtraChargeFields && (
        <div className="space-y-1.5 rounded-md border border-dashed border-border bg-muted/20 p-2">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Extra charge
            </p>
            {billStatus !== 'pushed' && (
              <button
                type="button"
                onClick={onRemoveExtraCharge}
                disabled={busy}
                title="Remove the extra charge row"
                className="text-[11px] text-muted-foreground hover:text-destructive disabled:opacity-40"
              >
                × Remove
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Charge name">
              <Input
                placeholder="Freight, Shipping, Handling, …"
                value={merged.extra_charge_name ?? ''}
                onChange={(e) => setField('extra_charge_name', e.target.value)}
                maxLength={100}
              />
            </Field>
            <Field
              label={
                (merged.extra_charge_name ?? '').trim() || 'Extra charges'
              }
            >
              <Input
                className="tabular-nums"
                inputMode="decimal"
                placeholder="0.00"
                value={merged.extra_charge_amount ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  // Empty input → null on the wire (DRF DecimalField
                  // is null=True). Anything else is shipped as a
                  // string; the backend normalises to Decimal.
                  setField('extra_charge_amount', v === '' ? null : v);
                }}
              />
            </Field>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Pushed to Xero as its own line item; not added to the
            Subtotal/GST/Total totals (the supplier's Subtotal
            already includes it).
          </p>
        </div>
      )}

      {showAddExtraChargeButton && (
        <button
          type="button"
          onClick={onRevealExtraCharge}
          disabled={busy}
          className="text-[11px] text-muted-foreground hover:text-foreground hover:underline underline-offset-2 disabled:opacity-40"
          title="Add a Freight / Shipping / Handling line picked up from the supplier's invoice"
        >
          + Add extra charge
        </button>
      )}
    </Section>
  );
}
